import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { FlaskConical, RefreshCw, Shield, CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MethodologyScoreBreakdown = ({ articleId }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (articleId) loadBreakdown();
  }, [articleId]);

  const loadBreakdown = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/articles/${articleId}/methodology-breakdown`);
      if (!response.ok) throw new Error('Failed to load methodology breakdown');
      setBreakdown(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-600">Loading methodology metrics...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-red-200 bg-red-50">
        <CardContent className="pt-6 text-center py-4">
          <p className="text-red-600">Failed to load methodology metrics</p>
          <Button onClick={loadBreakdown} variant="outline" size="sm" className="mt-2">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!breakdown) return null;

  // Not scorable (narrative reviews, expert opinions)
  if (breakdown.not_scorable) {
    return (
      <Card className="border-2 border-slate-200 bg-slate-50" data-testid="methodology-not-scorable">
        <CardHeader>
          <CardTitle className="text-lg text-slate-600 flex items-center gap-2">
            <FlaskConical className="w-5 h-5" />
            Methodology Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">{breakdown.not_scorable_reason || 'This article type does not receive methodology scoring.'}</p>
        </CardContent>
      </Card>
    );
  }

  const rawScore = breakdown.raw_methodology_score || 0;
  const finalScore = breakdown.total_methodology_score || 0;
  const percentComplete = (finalScore / 70) * 100;
  const hasIntegrity = breakdown.data_integrity_score != null;
  const rigor = breakdown.rigor_breakdown || {};

  const ScoreRow = ({ label, score, max, detail }) => (
    <div className="p-4 rounded-lg border bg-white border-blue-200 shadow-sm" data-testid={`score-row-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-semibold text-slate-900">{label}</h4>
          {detail && <p className="text-xs text-slate-500">{detail}</p>}
        </div>
        <div className="text-right">
          <span className="text-lg font-bold text-blue-600">{score}</span>
          <span className="text-sm text-slate-500">/{max}</span>
        </div>
      </div>
      <Progress value={(score / max) * 100} className="h-2 bg-blue-100" />
    </div>
  );

  const RigorItem = ({ label, points, active }) => (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        {active ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-slate-300" />}
        <span className={`text-sm ${active ? 'text-slate-900' : 'text-slate-400'}`}>{label}</span>
      </div>
      <span className={`text-sm font-semibold ${active ? 'text-green-600' : 'text-slate-300'}`}>
        {active ? `+${points}` : '0'}
      </span>
    </div>
  );

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50" data-testid="methodology-breakdown">
      <CardHeader className="border-b border-blue-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
            <FlaskConical className="w-5 h-5" />
            Methodology Score Breakdown
          </CardTitle>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600" data-testid="methodology-final-score">
              {finalScore.toFixed(1)}
            </div>
            <div className="text-sm text-slate-600">out of 70 points</div>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={percentComplete} className="h-3 bg-slate-200" />
          <div className="text-xs text-slate-500 mt-1 text-right">{percentComplete.toFixed(1)}%</div>
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-4">
        {/* 4 Component Scores */}
        <ScoreRow 
          label="Study Design" 
          score={breakdown.study_design_score} 
          max={30} 
          detail={`${breakdown.study_type} (${breakdown.evidence_level})`}
        />
        <ScoreRow 
          label="Sample Size" 
          score={breakdown.sample_size_score} 
          max={15} 
          detail={`${breakdown.sample_size || 0} participants`}
        />
        <ScoreRow 
          label="Follow-up Duration" 
          score={breakdown.followup_score} 
          max={15} 
          detail={breakdown.followup_months > 0 ? `${breakdown.followup_months} months` : 'No follow-up reported'}
        />

        {/* Methodological Rigor */}
        <div className="p-4 rounded-lg border bg-white border-blue-200 shadow-sm" data-testid="score-row-rigor">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-slate-900">Methodological Rigor</h4>
            <div className="text-right">
              <span className="text-lg font-bold text-blue-600">{breakdown.rigor_score}</span>
              <span className="text-sm text-slate-500">/10</span>
            </div>
          </div>
          <Progress value={(breakdown.rigor_score / 10) * 100} className="h-2 bg-blue-100 mb-3" />
          <div className="border-t border-slate-100 pt-2">
            <RigorItem label="Randomization reported" points={3} active={rigor.randomization_reported > 0} />
            <RigorItem label="Blinding reported" points={2} active={rigor.blinding_reported > 0} />
            <RigorItem label="Multicenter design" points={2} active={rigor.multicenter_design > 0} />
            <RigorItem label="Power calculation reported" points={2} active={rigor.power_calculation_reported > 0} />
            <RigorItem label="Clearly defined primary endpoint" points={1} active={rigor.clearly_defined_primary_endpoint > 0} />
          </div>
        </div>

        {/* Raw Score Summary */}
        <div className="p-3 bg-blue-100 border border-blue-200 rounded-lg flex items-center justify-between">
          <span className="text-sm font-semibold text-blue-900">Raw Methodology Score</span>
          <span className="text-lg font-bold text-blue-700" data-testid="raw-score">
            {rawScore.toFixed(1)}/70
          </span>
        </div>

        {/* Data Integrity Multiplier */}
        {hasIntegrity ? (
          <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50" data-testid="integrity-section">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-900">Data Integrity Check</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-green-600">
                  {breakdown.data_integrity_score}/100
                </span>
                {breakdown.data_integrity?.confidence_level && (
                  <Badge variant="outline" className="text-xs">
                    {breakdown.data_integrity.confidence_level}
                  </Badge>
                )}
              </div>
            </div>
            {breakdown.data_integrity?.assessment_explanation && (
              <p className="text-sm text-green-800 mb-2">{breakdown.data_integrity.assessment_explanation}</p>
            )}
            <div className="flex items-center justify-between bg-white p-2 rounded border border-green-200">
              <span className="text-xs text-slate-600">
                Multiplier: max(0.3, {breakdown.data_integrity_score}/100) = <strong>{breakdown.data_integrity_factor?.toFixed(2)}</strong>
              </span>
              <span className="text-xs text-slate-600">
                {rawScore.toFixed(1)} x {breakdown.data_integrity_factor?.toFixed(2)} = <strong className="text-blue-700">{finalScore.toFixed(1)}</strong>
              </span>
            </div>
            {breakdown.data_integrity?.red_flags?.length > 0 && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                <div className="flex items-center gap-1 text-xs text-red-700 font-semibold mb-1">
                  <AlertTriangle className="w-3 h-3" /> Red Flags
                </div>
                <ul className="text-xs text-red-600 ml-4 list-disc">
                  {breakdown.data_integrity.red_flags.map((flag, i) => (
                    <li key={i}>{flag}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 rounded-lg border-2 border-slate-200 bg-slate-50" data-testid="integrity-pending">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-slate-400" />
              <h4 className="font-semibold text-slate-600">Data Integrity Check</h4>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Not yet assessed. Re-running AI analysis will generate a Data Integrity assessment
              that acts as a multiplier on the raw score: <em>Final = Raw x max(0.3, Integrity/100)</em>
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <p className="text-xs text-indigo-800">
            <strong>Note:</strong> This scoring system evaluates methodological rigor of empirical clinical
            research. Narrative reviews and expert opinions are not scored. The Data Integrity score
            acts as a multiplier: Final = Raw Score x max(0.3, Integrity/100). This system supports
            rapid evidence discovery and does not replace peer review or GRADE/Cochrane frameworks.
          </p>
        </div>

        {breakdown.calculated_at && (
          <div className="text-xs text-slate-400 text-center">
            Last calculated: {new Date(breakdown.calculated_at).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MethodologyScoreBreakdown;
